import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdmin } from "../../services/helpers"
import { resolveDispute, addDisputeReply, getAdminDisputeById } from "../../services/disputes"
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserCircleIcon
} from "@heroicons/react/24/outline"

const AdminDisputeDetails = () => {
  const { disputeId } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [resolutionNote, setResolutionNote] = useState("")
  const [processing, setProcessing] = useState(false)
  const [replyMessage, setReplyMessage] = useState("")
  const [replying, setReplying] = useState(false)
  const [replies, setReplies] = useState([])
  const currentAdmin = getAdmin()

  const fetchData = async () => {
    setLoading(true)
    setError("")
    setSuccess("")
    try {
      const res = await getAdminDisputeById(disputeId)
      if (res.status === 200) {
        const disputeData = res.data
        setData(disputeData)
        // Load replies if they exist
        if (disputeData.replies && Array.isArray(disputeData.replies)) {
          setReplies(disputeData.replies)
        } else {
          setReplies([])
        }
      } else {
        setError(res.error || "Dispute not found")
      }
    } catch (err) {
      console.error('Error fetching dispute:', err)
      setError("Failed to fetch dispute details. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [disputeId])

  const handleResolve = async () => {
    if (!resolutionNote.trim()) {
      setError("Please provide resolution notes")
      return
    }
    if (!window.confirm("Confirm resolution of this dispute?")) return

    setProcessing(true)
    setError("")
    setSuccess("")
    try {
      const res = await resolveDispute(disputeId, resolutionNote)
      if (res.status === 200) {
        setSuccess("Dispute resolved successfully. Status updated to resolved.")
        setResolutionNote("")
        // Refresh data to get updated status
        await fetchData()
      } else {
        setError(res.error || "Failed to resolve dispute")
      }
    } catch (err) {
      setError("Failed to resolve dispute. Please try again.")
    }
    setProcessing(false)
  }

  const handleAddReply = async () => {
    if (!replyMessage.trim()) {
      setError("Please enter a reply message")
      return
    }

    setReplying(true)
    setError("")
    setSuccess("")
    try {
      const res = await addDisputeReply(disputeId, replyMessage)
      if (res.status === 200 || res.status === 201) {
        setSuccess("Reply added successfully")
        setReplyMessage("")
        // Refresh data to get updated replies from backend
        await fetchData()
      } else {
        setError(res.error || "Failed to add reply")
      }
    } catch (err) {
      setError("Failed to add reply. Please try again.")
      console.error('Reply error:', err)
    }
    setReplying(false)
  }

  if (loading) return <div className="text-center py-20">Loading...</div>
  if (!data) return <div className="text-center py-20">Dispute not found</div>

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/admin/disputes" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Disputes
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-8">
        {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl">{error}</div>}
        {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl">{success}</div>}

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{data.subject}</h1>
          <p className="text-slate-500">Order ID: <span className="font-mono text-slate-700">{data.orderId?._id || data.orderId || 'N/A'}</span></p>
          <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-bold uppercase ${data.status === 'resolved' ? 'bg-green-100 text-green-700' :
              data.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
            }`}>
            {data.status?.toUpperCase() || 'UNKNOWN'}
          </span>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-xl">
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">Message</h3>
            <p className="text-slate-800 whitespace-pre-wrap">{data.message || 'No message provided.'}</p>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              <span>Created by {data.createdBy?.name || data.createdBy?.email || 'User'}</span>
              {data.createdByRole && (
                <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-xs">{data.createdByRole}</span>
              )}
            </div>
            <span>•</span>
            <span>{new Date(data.createdAt).toLocaleString()}</span>
          </div>

          {data.resolvedBy && (
            <div className="bg-green-50 p-4 rounded-xl border border-green-200">
              <h3 className="text-xs font-bold text-green-700 uppercase mb-2">Resolution</h3>
              <p className="text-green-900 whitespace-pre-wrap">{data.resolutionNotes || 'Dispute resolved.'}</p>
              <p className="text-xs text-green-700 mt-2">
                Resolved by {data.resolvedBy?.name || 'Admin'} on {new Date(data.resolvedAt).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Replies/Thread Section */}
        <div className="mt-8 pt-6 border-t border-slate-100">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-5 h-5" />
            Conversation Thread
          </h3>
          
          {/* Replies List */}
          {replies.length > 0 && (
            <div className="space-y-4 mb-6">
              {replies.map((reply) => (
                <div key={reply._id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="flex items-start gap-3">
                    <UserCircleIcon className="w-6 h-6 text-slate-400 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">
                          {reply.createdBy?.name || 'Admin'}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {reply.createdBy?.role || 'ADMIN'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(reply.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-slate-700 whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Reply Form - Available for both open and resolved disputes */}
          {(
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Add Reply (Visible to Center and Attendee)
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
                className="w-full mb-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 px-3 py-2"
              />
              <button
                onClick={handleAddReply}
                disabled={replying || !replyMessage.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {replying ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          )}
        </div>

        {data.status === 'open' && (
          <div className="mt-8 pt-6 border-t border-slate-100">
            <h3 className="font-bold text-slate-900 mb-4">Resolve Dispute</h3>
            <textarea
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              placeholder="Enter resolution notes (required)..."
              rows={4}
              className="w-full mb-4 rounded-lg border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 px-3 py-2"
              required
            />
            <button
              onClick={handleResolve}
              disabled={processing || !resolutionNote.trim()}
              className="w-full py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? 'Resolving...' : 'Resolve Dispute'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDisputeDetails
